import type { SeoResultPageData } from '@/components/seo/seo-result-page-section';
import { getCompanyProfilePath } from '@/services/company-profile/routes';
import { slugifyJobText } from '@/services/jobs';

export function getAllJobsDescription(total = 0) {
  const formattedTotal = total.toLocaleString('vi-VN');

  return `Tìm việc làm mới nhất dành cho sinh viên, fresher và người đi làm trẻ. ${formattedTotal} tin tuyển dụng từ các công ty uy tín tại Hà Nội, Hồ Chí Minh và toàn quốc. Tạo CV miễn phí và ứng tuyển ngay trên Upzi.`;
}

export type JobSearchSeoCase =
  'keyword' | 'location' | 'keyword-location' | 'multi-location';

type JobSearchSeoDataInput = {
  case: JobSearchSeoCase;
  keyword: string;
  location: string;
  locationSlug?: string | null;
  total: number;
};

type JobDetailSeoDataInput = {
  companyAlias?: string;
  companyId?: string;
  companyName: string;
  jobTitle: string;
  location: string;
};

function getKeywordPath(
  keyword: string,
  location = '',
  locationSlug?: string | null
) {
  const keywordPath = `/viec-lam/${slugifyJobText(keyword)}`;
  const primaryLocation = location.split(',')[0]?.trim() ?? '';

  if (!primaryLocation) return keywordPath;

  const resolvedLocationSlug = locationSlug || slugifyJobText(primaryLocation);

  return resolvedLocationSlug
    ? `${keywordPath}-tai-${resolvedLocationSlug}`
    : keywordPath;
}

function getLocationPath(location: string, locationSlug?: string | null) {
  const slug = locationSlug || slugifyJobText(location);

  return `/viec-lam/tai-${slug}`;
}

function getJobDetailLocation(location: string) {
  const locationParts = location
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  return locationParts.at(-1) ?? '';
}

const ROOT_BREADCRUMB = [
  { href: '/', label: 'Trang chủ' },
  { href: '/viec-lam', label: 'Việc làm' },
] as const;

export function getJobSearchTitle({
  keyword = '',
  location = '',
  total,
}: {
  keyword?: string;
  location?: string;
  total?: number;
}) {
  const normalizedLocations = location
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
  const titleLocation =
    normalizedLocations.length === 1 ? normalizedLocations[0] : '';
  const titlePrefix =
    total === undefined
      ? 'Việc làm'
      : `${total.toLocaleString('vi-VN')} việc làm`;
  const titleSuffix =
    keyword || titleLocation
      ? `${keyword ? ` ${keyword}` : ''}${titleLocation ? ` tại ${titleLocation}` : ''}`
      : ' mới nhất';

  return `${titlePrefix}${titleSuffix}`;
}

export function getJobSearchLabel({
  keyword = '',
  location = '',
}: {
  keyword?: string;
  location?: string;
}) {
  const normalizedLocations = location
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
  const labelLocation =
    normalizedLocations.length === 1 ? normalizedLocations[0] : '';

  if (keyword && labelLocation)
    return `Việc làm ${keyword} tại ${labelLocation}`;
  if (keyword) return `Việc làm ${keyword}`;
  if (labelLocation) return `Việc làm tại ${labelLocation}`;

  return 'Việc làm';
}

export function getJobSearchDescription({
  keyword = '',
  location = '',
  total = 0,
}: {
  keyword?: string;
  location?: string;
  total?: number;
}) {
  const normalizedLocations = location
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
  const formattedTotal = total.toLocaleString('vi-VN');

  if (normalizedLocations.length > 1) {
    const subject = keyword ? ` ${keyword}` : '';

    return `Tìm việc làm${subject} tại nhiều tỉnh thành. ${formattedTotal} tin tuyển dụng mới nhất tại Upzi.`;
  }

  const normalizedLocation = normalizedLocations[0] ?? '';

  if (keyword && normalizedLocation) {
    return `Tìm việc làm ${keyword} tại ${normalizedLocation}. ${formattedTotal} tin tuyển dụng mới nhất tại Upzi.`;
  }

  if (keyword) {
    return `Tìm việc làm ${keyword} mới nhất. ${formattedTotal} tin tuyển dụng đang tuyển từ các công ty uy tín.`;
  }

  if (normalizedLocation) {
    return `Tìm việc làm tại ${normalizedLocation} mới nhất. ${formattedTotal} tin tuyển dụng mở từ các công ty uy tín.`;
  }

  return getAllJobsDescription(total);
}

export function getJobSearchKeywords({
  keyword = '',
  location = '',
}: {
  keyword?: string;
  location?: string;
} = {}) {
  const normalizedKeyword = keyword.trim();
  const normalizedLocations = location
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
  const primaryLocation =
    normalizedLocations.length === 1 ? normalizedLocations[0] : '';
  const values = [
    normalizedKeyword,
    normalizedKeyword ? `việc làm ${normalizedKeyword}` : '',
    normalizedKeyword ? `tuyển dụng ${normalizedKeyword}` : '',
    normalizedKeyword && primaryLocation
      ? `việc làm ${normalizedKeyword} tại ${primaryLocation}`
      : '',
    primaryLocation ? `việc làm tại ${primaryLocation}` : '',
    primaryLocation ? `tuyển dụng tại ${primaryLocation}` : '',
    normalizedLocations.length > 1 ? 'việc làm nhiều tỉnh thành' : '',
    'việc làm',
    'tìm việc làm',
    'tuyển dụng',
    'Upzi',
  ];

  return [...new Set(values.filter(Boolean))];
}

export function getJobSearchSeoData({
  case: seoCase,
  keyword,
  location,
  locationSlug,
  total,
}: JobSearchSeoDataInput): SeoResultPageData {
  const formattedTotal = new Intl.NumberFormat('vi-VN').format(total);
  const keywordPath = getKeywordPath(keyword, location, locationSlug);
  const locationPath = getLocationPath(location, locationSlug);

  if (seoCase === 'keyword') {
    return {
      breadcrumb: [...ROOT_BREADCRUMB, { label: `Việc làm ${keyword}` }],
      intro: {
        paragraph: [
          { text: `Khám phá ${formattedTotal} cơ hội việc làm ` },
          { href: keywordPath, text: keyword },
          {
            text: ' đang tuyển dụng trên toàn quốc. Upzi tổng hợp các vị trí ',
          },
          { href: keywordPath, text: keyword },
          {
            text: ' từ hàng nghìn nhà tuyển dụng uy tín, phù hợp cho sinh viên mới ra trường đến người có kinh nghiệm.',
          },
        ],
      },
    };
  }

  if (seoCase === 'location') {
    return {
      breadcrumb: [...ROOT_BREADCRUMB, { label: `Việc làm tại ${location}` }],
      intro: {
        paragraph: [
          { text: 'Tìm việc làm tại ' },
          { href: locationPath, text: location },
          {
            text: ` với ${formattedTotal} tin tuyển dụng mới nhất từ các công ty uy tín. Upzi tổng hợp việc làm tại `,
          },
          { href: locationPath, text: location },
          {
            text: ' từ hàng nghìn nhà tuyển dụng uy tín, phù hợp cho sinh viên mới ra trường đến người có kinh nghiệm.',
          },
        ],
      },
    };
  }

  if (seoCase === 'multi-location') {
    return {
      breadcrumb: [...ROOT_BREADCRUMB, { label: `Việc làm ${keyword}` }],
      intro: {
        paragraph: [
          { text: `Hiện có ${formattedTotal} vị trí ` },
          { href: keywordPath, text: keyword },
          {
            text: ' đang tuyển dụng tại nhiều tỉnh thành. Upzi tổng hợp các vị trí ',
          },
          { href: keywordPath, text: keyword },
          {
            text: ' từ hàng nghìn nhà tuyển dụng uy tín, phù hợp cho sinh viên mới ra trường đến người có kinh nghiệm.',
          },
        ],
      },
    };
  }

  return {
    breadcrumb: [
      ...ROOT_BREADCRUMB,
      { label: `Việc làm ${keyword} tại ${location}` },
    ],
    intro: {
      paragraph: [
        { text: `Hiện có ${formattedTotal} vị trí ` },
        { href: keywordPath, text: keyword },
        { text: ' đang tuyển dụng tại ' },
        { href: locationPath, text: location },
        { text: '. Upzi tổng hợp các vị trí ' },
        { href: keywordPath, text: keyword },
        {
          text: ' từ hàng nghìn nhà tuyển dụng uy tín, phù hợp cho sinh viên mới ra trường đến người có kinh nghiệm.',
        },
      ],
    },
  };
}

export function getJobDetailSeoData({
  companyAlias,
  companyId,
  companyName,
  jobTitle,
  location,
}: JobDetailSeoDataInput): SeoResultPageData {
  const primaryLocation = getJobDetailLocation(location);
  const keywordPath = getKeywordPath(jobTitle, primaryLocation);
  const locationPath = getLocationPath(primaryLocation);

  return {
    breadcrumb: [
      ...ROOT_BREADCRUMB,
      { href: keywordPath, label: `Việc làm ${jobTitle}` },
      { label: jobTitle },
    ],
    intro: {
      title: `Cơ hội việc làm ${jobTitle}`,
      paragraph: [
        { text: 'Nếu bạn đang tìm kiếm vị trí ' },
        { href: keywordPath, text: jobTitle },
        { text: ' tại ' },
        { href: locationPath, text: location },
        { text: ', đây là cơ hội làm việc tại ' },
        companyId
          ? {
              href: getCompanyProfilePath({
                alias: companyAlias,
                id: companyId,
              }),
              text: companyName,
            }
          : { emphasis: true, text: companyName },
        {
          text: ' với mức lương cạnh tranh và môi trường làm việc chuyên nghiệp. Ngoài tin tuyển dụng này, Upzi còn cập nhật nhiều việc làm cùng lĩnh vực và địa điểm mỗi ngày.',
        },
      ],
    },
  };
}
